import { commit } from "./candidate_column_order";
import { columnWidths, setColumnWidths } from "./candidate_column_widths";
import { columnMinWidth } from "./candidate_column_min_width";

const MIN_PX = 48;
const MAX_PX = 1200;
const KEY_STEP_PX = 16;
// Two presses on the same edge within this time are a double-click. Detected by hand: cancelling
// pointerdown (needed to stop text selection) also stops the browser's click and dblclick events.
const DOUBLE_PRESS_MS = 400;

const clamp = (px: number, min = 0): number => Math.min(MAX_PX, Math.max(MIN_PX, min, Math.round(px)));

let delegated = false;

interface Drag {
  table: HTMLTableElement;
  key: string;
  startX: number;
  startWidth: number;
  minWidth: number;
  moved: boolean;
}

let drag: Drag | null = null;
let lastPress: { key: string; at: number } | null = null;

function target(event: Event): { handle: HTMLElement; th: HTMLElement; table: HTMLTableElement; key: string } | null {
  const handle = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-col-resize]");
  const th = handle?.closest<HTMLElement>("th[data-col]");
  const table = th?.closest<HTMLTableElement>("table[data-widths]");
  const key = th?.dataset.col;
  return handle && th && table && key ? { handle, th, table, key } : null;
}

function setWidth(table: HTMLTableElement, key: string, px: number | null, minWidth = 0): void {
  const widths = columnWidths(table);
  if (px === null) delete widths[key];
  else widths[key] = clamp(px, minWidth);
  setColumnWidths(table, widths);
}

function onPointerDown(event: PointerEvent): void {
  if (event.button !== 0) return;
  const hit = target(event);
  if (!hit) return;

  event.preventDefault(); // no text selection, no column drag
  event.stopPropagation();

  const now = event.timeStamp;
  if (lastPress && lastPress.key === hit.key && now - lastPress.at < DOUBLE_PRESS_MS) {
    lastPress = null;
    setWidth(hit.table, hit.key, null);
    commit(hit.table);
    return;
  }
  lastPress = { key: hit.key, at: now };

  hit.handle.setPointerCapture(event.pointerId);
  drag = { table: hit.table, key: hit.key, startX: event.clientX, startWidth: hit.th.getBoundingClientRect().width,
    minWidth: columnMinWidth(hit.table, hit.key), moved: false };
  hit.table.classList.add("is-col-resizing");
}

function onPointerMove(event: PointerEvent): void {
  if (!drag) return;
  const dx = event.clientX - drag.startX;
  // a press that barely moves leaves an elastic column elastic (and may be half a double-click)
  if (!drag.moved && Math.abs(dx) <= 2) return;
  drag.moved = true;
  lastPress = null;
  setWidth(drag.table, drag.key, drag.startWidth + dx, drag.minWidth);
}

function onPointerUp(): void {
  if (!drag) return;
  const { table } = drag;
  drag = null;
  table.classList.remove("is-col-resizing");
  commit(table);
}

// Keyboard: arrow keys on a focused edge narrow or widen its column; Delete/Backspace resets it.
function onKeydown(event: KeyboardEvent): void {
  const hit = target(event);
  if (!hit) return;

  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    const step = event.key === "ArrowLeft" ? -KEY_STEP_PX : KEY_STEP_PX;
    setWidth(hit.table, hit.key, hit.th.getBoundingClientRect().width + step, columnMinWidth(hit.table, hit.key));
  } else if (event.key === "Delete" || event.key === "Backspace") {
    setWidth(hit.table, hit.key, null);
  } else {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  commit(hit.table);
}

export function initCandidateColumnResize(): void {
  if (delegated) return;
  delegated = true;

  document.addEventListener("pointerdown", onPointerDown);
  document.addEventListener("pointermove", onPointerMove);
  document.addEventListener("pointerup", onPointerUp);
  document.addEventListener("pointercancel", onPointerUp);
  document.addEventListener("keydown", onKeydown, true);
}
